import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { useStore, Workspace } from '../stores';
import { createWorkspaceApi, WorkspaceMember } from '../lib/api/workspaceApi';

/**
 * Hook for managing workspaces
 *
 * - Fetches workspaces on mount when authenticated
 * - Provides functions to create, update, delete workspaces
 * - Manages workspace members
 */
export function useWorkspaces() {
  const { config, getAccessToken, isAuthenticated } = useAuth();
  const { workspaces: storeWorkspaces, setWorkspaces, activeWorkspaceId, setActiveWorkspace } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ensure workspaces is always an array (defensive against corrupted localStorage)
  const workspaces = Array.isArray(storeWorkspaces) ? storeWorkspaces : [];

  // Create API client
  const api = useMemo(
    () => createWorkspaceApi(config, getAccessToken),
    [config, getAccessToken]
  );

  // Fetch workspaces
  const fetchWorkspaces = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await api.listWorkspaces();
      const validWorkspaces = (Array.isArray(data) ? data : []).filter((ws) => {
        if (!ws.id || !ws.name) {
          console.warn('Workspace missing required fields, filtering out:', ws);
          return false;
        }
        return true;
      });
      setWorkspaces(validWorkspaces);

      // Set active workspace if none selected
      if (!activeWorkspaceId && validWorkspaces.length > 0) {
        setActiveWorkspace(validWorkspaces[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch workspaces');
    } finally {
      setIsLoading(false);
    }
  }, [api, isAuthenticated, activeWorkspaceId, setWorkspaces, setActiveWorkspace]);

  // Create workspace
  const createWorkspace = useCallback(
    async (name: string, description?: string): Promise<Workspace | null> => {
      setError(null);
      try {
        const workspace = await api.createWorkspace({ name, description });
        setWorkspaces([...workspaces, workspace]);
        setActiveWorkspace(workspace.id);
        return workspace;
      } catch (err) {
        console.error('Failed to create workspace:', err);
        setError(err instanceof Error ? err.message : 'Failed to create workspace');
        return null;
      }
    },
    [api, workspaces, setWorkspaces, setActiveWorkspace]
  );

  // Update workspace
  const updateWorkspace = useCallback(
    async (
      workspaceId: string,
      updates: { name?: string; description?: string }
    ): Promise<boolean> => {
      setError(null);
      try {
        const updated = await api.updateWorkspace(workspaceId, updates);
        setWorkspaces(
          workspaces.map((w) => (w.id === workspaceId ? { ...w, ...updated } : w))
        );
        return true;
      } catch (err) {
        console.error('Failed to update workspace:', err);
        setError(err instanceof Error ? err.message : 'Failed to update workspace');
        return false;
      }
    },
    [api, workspaces, setWorkspaces]
  );

  // Delete workspace
  const deleteWorkspace = useCallback(
    async (workspaceId: string): Promise<boolean> => {
      setError(null);
      try {
        await api.deleteWorkspace(workspaceId);
        const newWorkspaces = workspaces.filter((w) => w.id !== workspaceId);
        setWorkspaces(newWorkspaces);

        // If deleted active workspace, select another
        if (activeWorkspaceId === workspaceId) {
          setActiveWorkspace(newWorkspaces[0]?.id || null);
        }
        return true;
      } catch (err) {
        console.error('Failed to delete workspace:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete workspace');
        return false;
      }
    },
    [api, workspaces, activeWorkspaceId, setWorkspaces, setActiveWorkspace]
  );

  // Get active workspace
  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.id === activeWorkspaceId) || null,
    [workspaces, activeWorkspaceId]
  );

  // Load on mount when authenticated
  useEffect(() => {
    if (isAuthenticated && workspaces.length === 0) {
      fetchWorkspaces();
    }
  }, [isAuthenticated, fetchWorkspaces, workspaces.length]);

  return {
    workspaces,
    activeWorkspace,
    activeWorkspaceId,
    setActiveWorkspace,
    isLoading,
    error,
    fetchWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
  };
}

/**
 * Hook for managing workspace members
 */
export function useWorkspaceMembers(workspaceId: string | null) {
  const { config, getAccessToken } = useAuth();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const api = useMemo(
    () => createWorkspaceApi(config, getAccessToken),
    [config, getAccessToken]
  );

  // Fetch members
  const fetchMembers = useCallback(async () => {
    if (!workspaceId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await api.listMembers(workspaceId);
      setMembers(data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch members');
    } finally {
      setIsLoading(false);
    }
  }, [api, workspaceId]);

  // Add member
  const addMember = useCallback(
    async (email: string, role: 'admin' | 'member'): Promise<boolean> => {
      if (!workspaceId) return false;

      setError(null);
      try {
        const member = await api.addMember(workspaceId, { email, role });
        setMembers([...members, member]);
        return true;
      } catch (err) {
        console.error('Failed to add member:', err);
        setError(err instanceof Error ? err.message : 'Failed to add member');
        return false;
      }
    },
    [api, workspaceId, members]
  );

  // Fetch on workspace change
  useEffect(() => {
    if (workspaceId) {
      fetchMembers();
    } else {
      setMembers([]);
    }
  }, [workspaceId, fetchMembers]);

  return {
    members,
    isLoading,
    error,
    fetchMembers,
    addMember,
  };
}

export default useWorkspaces;
